import { useState } from "react";
import Script from "next/script";
import { Button } from "@/shared/components/ui";
import LoadingSpinner from "@/shared/components/LoadingSpinner";
import { cn } from "@/shared/utils";
import { PublicConfig } from "@/shared/PublicConfig";
import { Quote } from "@/shared/types/Quote.interface";
import { UIUtils } from "@/shared/utils/UIUtils";
import Strings from "@/shared/utils/Strings.constants";
import { useChasePayment } from "@/quote-ptz-ca/src/hooks/useChasePayment";

interface ChasePaymentIframeProps {
    quote: Quote;
    /** Iframe overlaid with a spinner and made non-interactive. */
    disabled?: boolean;
}

// Static input, so format once at module scope. The failed-state copy renders this inside a tel: link and
// appends PTZ_CA.HOURS verbatim — HOURS already carries "EST" (unlike PTZ_US.HOURS), so appending
// UIUtils.getPhoneHours' live zone abbreviation would render a contradictory "EST EDT" during daylight
// saving. HOURS' embedded "\n" renders via whitespace-pre-line.
const CALL_CENTER_PHONE_DISPLAY = UIUtils.formatPhone(Strings.PTZ_CA.PHONE_NUMBER);

/**
 * Chase Hosted Payment Form (HPF) - Loads the parent bridge script, requests a "uid", then mounts Chase-hosted iframe
 * On successful card capture (`completePayment`), the hook finalizes the quote
 */
export function ChasePaymentIframe({ quote, disabled = false }: ChasePaymentIframeProps) {
    const [scriptReady, setScriptReady] = useState(false);
    const [scriptError, setScriptError] = useState(false);

    const { uid, isFetchingUid, error, finalizeStatus, reloadPaymentForm } = useChasePayment({ quoteId: quote.id });

    // React Query holds the PREVIOUS `data`/`error` across a refetch, so both must ignore them while one
    // is in flight and show the spinner instead. Without it, "Reload payment form" remounts the iframe
    // against the uid the declined attempt already consumed (Chase renders "Missing or Invalid UID!")
    // until the replacement lands, and the load-error state leaves the error on screen with no sign
    // anything is happening. A refetch that fails restores the error UI on its own.
    // `scriptError` is independent of the uid request, so it is not deferred.
    const hasError = (!!error && !isFetchingUid) || scriptError;
    const isReady = scriptReady && !!uid && !isFetchingUid;

    const handleReloadPaymentForm = () => {
        // A uid-request failure recovers with a fresh uid fetch. But if the parent bridge script
        // failed to load, refetching won't re-run it (the <Script> stays mounted and onReady won't refire), so fall back to a full page reload to re-fetch script + uid from scratch.
        if (scriptError) {
            window.location.reload();
            return;
        }
        reloadPaymentForm();
    };

    return (
        <div id="chase-iframe-wrapper">
            {/* Parent bridge script required for the parent page to communicate with the hosted iframe.
                onReady (not onLoad) fires on first load AND on remounts, so it survives StrictMode double-mount / cached loads. */}
            <Script
                src={PublicConfig.PTZ_CA.PAYMENT_LOAD_SCRIPT_URL}
                strategy="afterInteractive"
                onReady={() => setScriptReady(true)}
                onError={e => {
                    console.error("[chase_hpf] parent script failed to load", e);
                    setScriptError(true);
                }}
            />

            {finalizeStatus === "finalizing" ? (
                <div className="flex justify-center py-10">
                    <LoadingSpinner />
                </div>
            ) : finalizeStatus === "declined" ? (
                <div className="flex flex-col items-start gap-3">
                    <p className="text-content-danger" role="alert">
                        Your payment couldn’t be authorized. Please try again with a different payment method.
                    </p>
                    <Button variant="outline-secondary" size="sm" onClick={handleReloadPaymentForm}>
                        {Strings.PTZ_CA.RELOAD_PAYMENT_FORM}
                    </Button>
                </div>
            ) : finalizeStatus === "missing_token" ? (
                <div className="flex flex-col items-start gap-3">
                    <p className="text-content-danger" role="alert">
                        Error spotted! Please re-enter your payment details to try again.
                    </p>
                    <Button variant="outline-secondary" size="sm" onClick={handleReloadPaymentForm}>
                        {Strings.PTZ_CA.RELOAD_PAYMENT_FORM}
                    </Button>
                </div>
            ) : finalizeStatus === "failed" ? (
                // Finalize errored AND the status poll stayed inconclusive — we can't confirm whether the
                // charge went through. Deliberately NO self-serve re-attempt here (reload = fresh session +
                // new card = double-charge risk on a possibly-paid quote); route to the call center, which
                // can reconcile the payment and complete enrollment.
                <div className="mb-4 flex flex-col items-start gap-3">
                    <p className="whitespace-pre-line text-content-danger" role="alert">
                        Error spotted! We couldn’t confirm your payment. Please give us a call to complete your enrollment:{" "}
                        <a className="underline" href={`tel:${Strings.PTZ_CA.PHONE_NUMBER}`}>
                            {CALL_CENTER_PHONE_DISPLAY}
                        </a>
                        . {Strings.PTZ_CA.HOURS}
                    </p>
                </div>
            ) : hasError ? (
                <div className="flex flex-col items-center gap-3 px-4 py-6">
                    <p className="text-content-danger" role="alert">
                        There was an issue loading the payment form.
                    </p>
                    <Button size="sm" variant="default-rounded" onClick={handleReloadPaymentForm}>
                        {Strings.PTZ_CA.RELOAD_PAYMENT_FORM}
                    </Button>
                </div>
            ) : isReady ? (
                <div className="relative">
                    {disabled && (
                        <div className="absolute inset-0 z-10 flex items-center justify-center" role="status" aria-label="Updating">
                            <LoadingSpinner />
                        </div>
                    )}
                    <iframe
                        title="Checkout payment form"
                        src={`${PublicConfig.PTZ_CA.PAYMENT_IFRAME_URL}?uid=${encodeURIComponent(uid!)}`}
                        className={cn("h-[660px] w-full border-0", disabled && "pointer-events-none opacity-50")}
                    />
                </div>
            ) : (
                <div className="flex justify-center py-10" role="status" aria-label="Loading payment form">
                    <LoadingSpinner />
                </div>
            )}
        </div>
    );
}
